import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import RestaurantFormGeneral from "@/components/restaurant/RestaurantFormGeneral";
import RestaurantFormOpeningHours from "@/components/restaurant/RestaurantFormOpeningHours";
import RestaurantFormMedia from "@/components/restaurant/RestaurantFormMedia";
import RestaurantFormMenus from "@/components/restaurant/RestaurantFormMenus";
import RestaurantFormDelivery from "@/components/restaurant/RestaurantFormDelivery";
import { Restaurant } from "@/lib/types";

const RestaurantCreatePage = () => {
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState("allgemein");
  // Diese Setter werden in Props verwendet
  const [_isDirty, setIsDirty] = useState(false);
  const [_isSaving, setIsSaving] = useState(false);
  
  // Erstelle ein leeres Restaurant-Objekt als Platzhalter
  const emptyRestaurant: Restaurant = {
    id: "new",
    name: "",
    slug: "",
    cuisineId: 1,
    priceRange: 2,
    description: "",
    street: "",
    houseNumber: "",
    postalCode: "",
    city: "",
    country: "Deutschland",
    state: "",
    phone: "",
    email: "",
    website: "",
    facebook: "",
    instagram: "",
    latitude: 52.52, // Berlin
    longitude: 13.41,
    enabled: true,
    openingHours: [],
    deliveryHours: [],
    images: []
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header mit Breadcrumb Navigation */}
      <header className="bg-neutral-darker py-6 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl font-bold text-white">Neues Restaurant hinzufügen</h1>
              <Breadcrumb className="mt-1 text-gray-300">
                <BreadcrumbItem>
                  <BreadcrumbLink href="/admin">Dashboard</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="/admin/restaurants">Restaurants</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink>Neu</BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
                onClick={() => navigate("/admin/restaurants")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="border-b px-6 py-4 sticky top-0 bg-white z-20 shadow-sm">
              <TabsList className="w-full flex-wrap lg:flex-nowrap">
                <TabsTrigger value="allgemein" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white">
                  Allgemein
                </TabsTrigger>
                <TabsTrigger value="oeffnungszeiten" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white">
                  Öffnungszeiten
                </TabsTrigger>
                <TabsTrigger value="lieferung" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white">
                  Lieferung
                </TabsTrigger>
                <TabsTrigger value="bilder" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white">
                  Bilder
                </TabsTrigger>
                <TabsTrigger value="speisekarten" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white">
                  Speisekarten
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6">
              <TabsContent value="allgemein" className="mt-0">
                <RestaurantFormGeneral 
                  restaurant={emptyRestaurant} 
                  onDirtyChange={setIsDirty} 
                  onSavingChange={setIsSaving}
                />
              </TabsContent>
              
              <TabsContent value="oeffnungszeiten" className="mt-0">
                <RestaurantFormOpeningHours 
                  restaurant={emptyRestaurant} 
                  onDirtyChange={setIsDirty} 
                  onSavingChange={setIsSaving}
                />
              </TabsContent>
              
              <TabsContent value="lieferung" className="mt-0">
                <RestaurantFormDelivery 
                  restaurant={emptyRestaurant} 
                  onDirtyChange={setIsDirty} 
                  onSavingChange={setIsSaving}
                />
              </TabsContent>
              
              <TabsContent value="bilder" className="mt-0">
                <RestaurantFormMedia 
                  restaurant={emptyRestaurant} 
                  onDirtyChange={setIsDirty} 
                  onSavingChange={setIsSaving}
                />
              </TabsContent>
              
              <TabsContent value="speisekarten" className="mt-0">
                <RestaurantFormMenus 
                  restaurant={emptyRestaurant} 
                  onDirtyChange={setIsDirty} 
                  onSavingChange={setIsSaving}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default RestaurantCreatePage;